// mobile/src/lib/photo-integrity.ts

interface IntegrityParams {
  latitude: number;
  longitude: number;
  timestamp: string;
  deviceFingerprint: string;
  promoterId: string;
  nonce: number;
}

/**
 * Generate device fingerprint from browser APIs.
 * Not cryptographically unique, but enough to detect phone swaps.
 */
export function generateDeviceFingerprint(): string {
  const parts = [
    navigator.userAgent,
    `${screen.width}x${screen.height}`,
    String(navigator.hardwareConcurrency || 0),
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    navigator.languages?.join(',') || navigator.language || '',
  ];
  return parts.join('|');
}

/**
 * Compute SHA-256 integrity hash from photo blob + metadata.
 * Any change to the photo or metadata produces a different hash.
 */
export async function computeIntegrityHash(
  blob: Blob,
  params: IntegrityParams
): Promise<string> {
  const photoBytes = await blob.arrayBuffer();
  const metaString = [
    params.latitude.toFixed(6),
    params.longitude.toFixed(6),
    params.timestamp,
    params.deviceFingerprint,
    params.promoterId,
    String(params.nonce),
  ].join('|');

  const metaBytes = new TextEncoder().encode(metaString);

  // Concatenate photo bytes + meta bytes
  const combined = new Uint8Array(photoBytes.byteLength + metaBytes.byteLength);
  combined.set(new Uint8Array(photoBytes), 0);
  combined.set(metaBytes, photoBytes.byteLength);

  const hashBuffer = await crypto.subtle.digest('SHA-256', combined);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

const NONCE_KEY = 'photo_nonce_counter';

/**
 * Get and increment the sequential nonce for this device.
 * Stored in localStorage (persists across IndexedDB clears).
 */
export function getNextNonce(): number {
  const current = parseInt(localStorage.getItem(NONCE_KEY) || '0', 10);
  const next = current + 1;
  localStorage.setItem(NONCE_KEY, String(next));
  return next;
}

/**
 * Get current nonce without incrementing.
 */
export function getCurrentNonce(): number {
  return parseInt(localStorage.getItem(NONCE_KEY) || '0', 10);
}
